import { DocumentView, WorkflowState } from '@/types/workflows'
import { KeyboardShortcutGroup } from '@/components/keyboard-shortcuts-popover/KeyboardShortcutsDialog'
import { altHotKeyStr, metaHotKeyStr, modHotKeyStr } from './os'

export function getShortcuts(
  documentView?:
    | WorkflowState['default_document_view']
    | 'lite-idle'
    | 'lite-processing',
) {
  if (documentView === 'lite-processing') {
    return []
  }

  const documentShortcuts: [string, string][] = [
    ['Close Document', 'Esc'],
    ['Previous Document', `${altHotKeyStr} + 1`],
    ['Next Document', `${altHotKeyStr} + 2`],
    ['Previous Page', `${altHotKeyStr} + 3 / ${metaHotKeyStr} + Scroll Up`],
    ['Next Page', `${altHotKeyStr} + 4 / ${metaHotKeyStr} + Scroll Down`],
  ]

  if (documentView !== 'lite-idle') {
    documentShortcuts.push(
      ['Primary Action', `${altHotKeyStr} + D`],
      ['Stay on Document on Event', 'Hold Shift'],
      ['Hold to Hide Chips', `${altHotKeyStr} + V`],
    )

    if (documentView === 'validation') {
      documentShortcuts.push(
        ['Toggle Layout', `${altHotKeyStr} + O`],
        ['Field Quick Select', `${altHotKeyStr} + Click`],
      )
    }
  }

  const shortcuts: KeyboardShortcutGroup[] = [
    {
      title: 'Document Shortcuts',
      shortcuts: documentShortcuts.reduce(
        (acc, [title, shortcut]) => ({ ...acc, [title]: shortcut }),
        {},
      ),
    },
  ]

  if (documentView === 'validation') {
    shortcuts.push({
      title: 'Field Shortcuts',
      shortcuts: {
        'Clear Selected Field Chips': `${modHotKeyStr} + Del`,
        'Cycle Through Favorite Fields': `Ctrl + ${metaHotKeyStr} + Tab`,
        'Multi-Select': 'Shift + Click and Drag',
        'Multi-Deselect': `Shift + ${altHotKeyStr} + Click and Drag`,
      },
    })
  }

  if (documentView === 'validation' || documentView === 'lite-idle') {
    const tableShortcuts: Record<string, string> = {
      'Insert Row Above': `${modHotKeyStr} + ${altHotKeyStr} + Shift + Up`,
      'Insert Row Below': `${modHotKeyStr} + ${altHotKeyStr} + Shift + Down`,
      'Delete Row': `${modHotKeyStr} + ${altHotKeyStr} + Shift + Del`,
    }

    if (documentView === 'validation') {
      tableShortcuts[
        'Row Multi-Select'
      ] = `${metaHotKeyStr} + ${altHotKeyStr} + Click and Drag`
      tableShortcuts[
        'Smart Row Select'
      ] = `${metaHotKeyStr} + ${altHotKeyStr} + Shift + Click and Drag`
    }
    shortcuts.push({
      title: 'Table Shortcuts',
      shortcuts: tableShortcuts,
    })
  }
  return shortcuts
}

/** Query param that opens the document overlay on a specific view instead of the workflow state's default. */
export const DOCUMENT_VIEW_PARAM = 'document_view'

const DOCUMENT_VIEWS: DocumentView[] = [
  'validation',
  'comparison',
  'split-document',
  'purchase-order',
  'approvals',
]

/** The view a `document_view` param asks for, or undefined when it's missing or isn't a view. */
export function parseDocumentViewParam(
  value: string | null,
): DocumentView | undefined {
  return DOCUMENT_VIEWS.find((view) => view === value)
}

/**
 * The URL that opens a document on a specific view. The workflow and state
 * are included when known, so the page doesn't have to redirect to find them.
 */
export function getDocumentViewUrl({
  documentId,
  projectId,
  workflowState,
  view,
}: {
  documentId: string
  projectId: string
  workflowState?: Pick<WorkflowState, 'id' | 'workflow_id'>
  view: DocumentView
}) {
  const params = new URLSearchParams()
  if (workflowState) {
    params.set('workflow', workflowState.workflow_id)
    params.set('workflow_state', workflowState.id)
  }
  params.set('document_id', documentId)
  params.set(DOCUMENT_VIEW_PARAM, view)
  return `/projects/${projectId}/documents?${params.toString()}`
}
